/**
 * useFuturesSpotMarkets Hook
 *
 * React hook for market discovery with automatic caching and error handling.
 * Provides futures-spot symbol pairs for arbitrage opportunities.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { MarketDiscoveryService } from '../services/marketDiscovery';
import {
  MarketDiscoveryCache,
  StaleWhileRevalidate,
} from '../services/marketDiscoveryCache';
import type { FuturesSpotSymbol } from '../types/futuresSpot';

/**
 * Hook state interface
 */
interface UseFuturesSpotMarketsResult {
  /** Discovered futures-spot pairs */
  markets: FuturesSpotSymbol[];

  /** Loading state (true on initial load or force refresh) */
  loading: boolean;

  /** Error message if discovery failed */
  error: string | null;

  /** Whether data is stale (from cache, revalidating in background) */
  isStale: boolean;

  /** Manually refresh markets (bypasses cache) */
  refresh: () => Promise<void>;

  /** Cache metrics */
  cacheMetrics: {
    hits: number;
    misses: number;
    hitRate: number;
  };
}

/**
 * Hook configuration
 */
interface UseFuturesSpotMarketsConfig {
  /** Enable automatic market discovery on mount (default: true) */
  enabled?: boolean;

  /** Enable debug logging (default: false) */
  enableLogging?: boolean;

  /** Maximum retry attempts on failure (default: 3) */
  maxRetries?: number;
}

const DEFAULT_CONFIG: Required<UseFuturesSpotMarketsConfig> = {
  enabled: true,
  enableLogging: false,
  maxRetries: 3,
};

/**
 * Custom hook for futures-spot market discovery
 *
 * @param config - Hook configuration
 * @returns Market discovery state and controls
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { markets, loading, error, refresh } = useFuturesSpotMarkets();
 *
 *   if (loading) return <div>Loading markets...</div>;
 *   if (error) return <div>Error: {error}</div>;
 *
 *   return (
 *     <div>
 *       <button onClick={refresh}>Refresh</button>
 *       <ul>
 *         {markets.map(m => (
 *           <li key={m.normalizedSymbol}>{m.baseAsset}/{m.quoteAsset}</li>
 *         ))}
 *       </ul>
 *     </div>
 *   );
 * }
 * ```
 */
export function useFuturesSpotMarkets(
  config: UseFuturesSpotMarketsConfig = {}
): UseFuturesSpotMarketsResult {
  const fullConfig = { ...DEFAULT_CONFIG, ...config };

  // State
  const [markets, setMarkets] = useState<FuturesSpotSymbol[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isStale, setIsStale] = useState<boolean>(false);
  const [cacheMetrics, setCacheMetrics] = useState({
    hits: 0,
    misses: 0,
    hitRate: 0,
  });

  // Service instances (created once, stable across renders)
  const serviceRef = useRef<MarketDiscoveryService | null>(null);
  const cacheRef = useRef<MarketDiscoveryCache | null>(null);
  const swr = useRef<StaleWhileRevalidate<FuturesSpotSymbol[]> | null>(null);

  // Retry state
  const retryCount = useRef<number>(0);

  /**
   * Initialize services on mount
   */
  useEffect(() => {
    if (!serviceRef.current) {
      serviceRef.current = new MarketDiscoveryService({
        enableLogging: fullConfig.enableLogging,
      });
    }

    if (!cacheRef.current) {
      cacheRef.current = new MarketDiscoveryCache();
    }

    if (!swr.current && serviceRef.current && cacheRef.current) {
      swr.current = new StaleWhileRevalidate(
        () => serviceRef.current!.discoverFuturesSpotPairs(),
        cacheRef.current
      );
    }
  }, [fullConfig.enableLogging]);

  /**
   * Discover markets with retry logic
   */
  const discoverMarkets = useCallback(async () => {
    if (!swr.current) {
      console.error('[useFuturesSpotMarkets] SWR not initialized');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await swr.current.get();

      setMarkets(result.data);
      setIsStale(result.isStale);
      retryCount.current = 0; // Reset on success

      // Update cache metrics
      if (cacheRef.current) {
        setCacheMetrics(cacheRef.current.getMetrics());
      }

      if (fullConfig.enableLogging) {
        console.info(
          `[useFuturesSpotMarkets] Discovered ${result.data.length} markets (stale: ${result.isStale})`
        );
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';

      // Retry on failure
      if (retryCount.current < fullConfig.maxRetries) {
        retryCount.current++;
        const delay = Math.pow(2, retryCount.current - 1) * 1000; // Exponential backoff: 1s, 2s, 4s

        console.warn(
          `[useFuturesSpotMarkets] Discovery failed (attempt ${retryCount.current}/${fullConfig.maxRetries}), retrying in ${delay}ms...`
        );

        setTimeout(() => {
          discoverMarkets();
        }, delay);

        return;
      }

      // Max retries reached
      console.error('[useFuturesSpotMarkets] Discovery failed after max retries:', err);
      setError(errorMessage);
      retryCount.current = 0;
    } finally {
      setLoading(false);
    }
  }, [fullConfig.enableLogging, fullConfig.maxRetries]);

  /**
   * Manual refresh (bypasses cache)
   */
  const refresh = useCallback(async () => {
    if (!swr.current) {
      console.error('[useFuturesSpotMarkets] SWR not initialized');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const freshData = await swr.current.refresh();

      setMarkets(freshData);
      setIsStale(false);
      retryCount.current = 0;

      // Update cache metrics
      if (cacheRef.current) {
        setCacheMetrics(cacheRef.current.getMetrics());
      }

      if (fullConfig.enableLogging) {
        console.info(`[useFuturesSpotMarkets] Force refreshed ${freshData.length} markets`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('[useFuturesSpotMarkets] Refresh failed:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [fullConfig.enableLogging]);

  /**
   * Discover markets on mount if enabled
   */
  useEffect(() => {
    if (fullConfig.enabled) {
      discoverMarkets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullConfig.enabled]);

  return {
    markets,
    loading,
    error,
    isStale,
    refresh,
    cacheMetrics,
  };
}
